import { useMemo } from 'react';

import { useAppSelector } from '@store/store.tsx';

export interface PosPermissions {
  isSuperuser: boolean;
  canApplyLineDiscount: boolean;
  canApplyOrderDiscount: boolean;
  canOverrideCommission: boolean;
  canEditPrice: boolean;
  canViewRestrictedCategories: boolean;
}

const normalizeToken = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }
  const trimmed = value.trim().toLowerCase();
  return trimmed ? trimmed : null;
};

const hasAny = (groups: Set<string>, candidates: string[]): boolean => {
  for (const candidate of candidates) {
    if (groups.has(candidate.toLowerCase())) {
      return true;
    }
  }
  return false;
};

export const computePosPermissions = (
  groups: string[] | undefined,
  roles: string[] | undefined,
  permissions?: string[] | undefined,
): PosPermissions => {
  const normalizedGroups = new Set(
    (groups ?? [])
      .map(normalizeToken)
      .filter((entry): entry is string => Boolean(entry)),
  );

  const normalizedRoles = new Set(
    (roles ?? [])
      .map(normalizeToken)
      .filter((entry): entry is string => Boolean(entry)),
  );

  const normalizedPermissions = new Set(
    (permissions ?? [])
      .map(normalizeToken)
      .filter((entry): entry is string => Boolean(entry)),
  );

  const isSuperuser = normalizedRoles.has('superuser');
  const isAdmin =
    isSuperuser ||
    normalizedRoles.has('admin') ||
    hasAny(normalizedGroups, ['admin', 'erp:staff:admin', 'staff:admin', 'pos:admin']);

  const canOverrideCommission =
    isAdmin ||
    hasAny(normalizedGroups, ['pos:admin', 'pos:commissions', 'pos:commissions:override']) ||
    hasAny(normalizedPermissions, ['pos:commissions:override']);

  const canApplyLineDiscount =
    isAdmin ||
    hasAny(normalizedGroups, [
      'admin',
      'erp:staff:admin',
      'staff:admin',
      'pos:admin',
      'pos:discounts',
      'sale_payments:discounts',
      'pos:discounts:line',
    ]) ||
    hasAny(normalizedPermissions, ['pos:discounts', 'sale_payments:discounts', 'pos:discounts:line', 'pos:admin']);

  const canApplyOrderDiscount =
    isAdmin ||
    hasAny(normalizedGroups, [
      'admin',
      'erp:staff:admin',
      'staff:admin',
      'pos:admin',
      'pos:discounts',
      'sale_payments:discounts',
      'pos:discounts:order',
    ]) ||
    hasAny(normalizedPermissions, ['pos:discounts', 'sale_payments:discounts', 'pos:discounts:order', 'pos:admin']);

  const canEditPrice =
    isAdmin ||
    hasAny(normalizedGroups, ['pos:admin', 'pos:pricing']) ||
    hasAny(normalizedPermissions, ['pos:pricing']);

  const canViewRestrictedCategories =
    isAdmin ||
    hasAny(normalizedGroups, ['pos:admin', 'pos:all']) ||
    Array.from(normalizedGroups).some((group) => group.startsWith('pos:category:'));

  return {
    isSuperuser,
    canApplyLineDiscount,
    canApplyOrderDiscount,
    canOverrideCommission,
    canEditPrice,
    canViewRestrictedCategories,
  };
};

export const usePosPermissions = (): PosPermissions => {
  const profile = useAppSelector((state) => state.user.profile);
  const userPermissions = useAppSelector((state) => state.user.permissions);
  const groups = profile?.groups ?? [];
  const roles = profile?.role ?? [];

  const groupKey = [...groups].sort().join('|');
  const roleKey = [...roles].sort().join('|');
  const permKey = [...userPermissions].sort().join('|');

  return useMemo(
    () => computePosPermissions(groups, roles, userPermissions),
    [groupKey, roleKey, permKey],
  );
};

